import { Controller } from "@hotwired/stimulus"

// Sidebar highlight + smooth scroll for application document section anchors (#section-id).
// Expects: nav links with data-section-spy-target="link" and data-section-id="section-12",
// and h2 (or [id^="section-"]) inside the same controller element.
export default class extends Controller {
  static targets = ["link"]
  static values = { documentTabKey: { type: String, default: "document" } }

  connect() {
    this.onDocumentTab = () => {
      this.setupObserver()
      this.refreshHighlightFromHash()
    }
    this.onWindowHash = () => this.refreshHighlightFromHash()
    window.addEventListener("application:document-tab-shown", this.onDocumentTab)
    window.addEventListener("hashchange", this.onWindowHash)

    if (!this._panelIsHidden()) {
      this.setupObserver()
    }
    this.refreshHighlightFromHash()
  }

  disconnect() {
    window.removeEventListener("application:document-tab-shown", this.onDocumentTab)
    window.removeEventListener("hashchange", this.onWindowHash)
    this._disconnectObserver()
  }

  navigate(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.sectionId
    if (!id) return

    const fullHref = event.currentTarget.getAttribute("href")
    this._ensureDocumentTab()

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id)
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" })
        }
        if (fullHref) {
          history.pushState(null, "", fullHref)
        }
        this._setActiveLink(id)
      })
    })
  }

  _ensureDocumentTab() {
    const tabsRoot = this.element.closest("[data-controller~='tabs']")
    if (!tabsRoot) return

    const docBtn = tabsRoot.querySelector(`[data-key="${this.documentTabKeyValue}"]`)
    if (docBtn) docBtn.click()
  }

  _panelIsHidden() {
    return Boolean(this.element.closest(".hidden"))
  }

  _disconnectObserver() {
    this.observer?.disconnect()
    this.observer = null
  }

  setupObserver() {
    this._disconnectObserver()

    const sections = this.element.querySelectorAll("[id^='section-']")
    if (sections.length === 0) return

    this.observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.filter((e) => e.isIntersecting)
        if (intersecting.length === 0) return

        intersecting.sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        const id = intersecting[0].target.id
        this._setActiveLink(id)
      },
      {
        root: null,
        rootMargin: "-10% 0px -52% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 1]
      }
    )
    sections.forEach((el) => this.observer.observe(el))
  }

  _inactiveLinkClass() {
    return "flex w-full items-center rounded-md px-3 py-1.5 text-left text-zinc-700 transition-colors hover:bg-zinc-100"
  }

  _activeLinkClass() {
    return "flex w-full items-center rounded-md bg-zinc-900 px-3 py-1.5 text-left font-medium text-white"
  }

  _setActiveLink(id) {
    this.linkTargets.forEach((link) => {
      const isActive = link.dataset.sectionId === id
      link.className = isActive ? this._activeLinkClass() : this._inactiveLinkClass()
    })
  }

  refreshHighlightFromHash() {
    let id = window.location.hash?.replace(/^#/, "")
    if (!id || !id.startsWith("section-") || !document.getElementById(id)) {
      const first = this.element.querySelector("[id^='section-']")
      id = first?.id
    }
    if (id) this._setActiveLink(id)
  }
}
